import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { event } from "@/lib/analytics";

import { leftItems, type FaqItem } from "./FaqSection.data";

const INITIAL_VISIBLE_DESKTOP = 10;
const INITIAL_VISIBLE_MOBILE = 5;

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile(mql.matches);
    update();
    mql.addEventListener("change", update);
    return () => mql.removeEventListener("change", update);
  }, []);
  return isMobile;
}

export function FaqSection() {
  const [showAll, setShowAll] = useState(false);
  const isMobile = useIsMobile();
  const initialVisible = isMobile ? INITIAL_VISIBLE_MOBILE : INITIAL_VISIBLE_DESKTOP;
  const visible = showAll ? leftItems : leftItems.slice(0, initialVisible);

  const col1: FaqItem[] = [];
  const col2: FaqItem[] = [];
  visible.forEach((item, i) => {
    if (i % 2 === 0) col1.push(item);
    else col2.push(item);
  });

  return (
    <section id="faq" className="bg-background pt-12 pb-0 sm:pt-16">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="h-px w-full bg-gradient-to-r from-transparent via-foreground/15 to-transparent" />
      </div>
      <div className="pt-12 sm:pt-16 md:pt-16">
      <div className="mx-auto max-w-6xl px-5 md:px-6">
        <div className="mx-auto max-w-5xl">
          <p className="text-center text-xs font-bold uppercase tracking-[0.22em] text-brand-blue">
            FAQ
          </p>
          <h2 className="mt-4 text-center text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-5xl">
            Frequently <br className="sm:hidden" />
            asked{" "}
            <span className="bg-gradient-to-r from-brand-blue to-[#ec4899] bg-clip-text text-transparent">
              questions
            </span>
          </h2>
          <div className="mt-5 flex flex-col items-center justify-center gap-5">
            <p className="text-center text-base text-muted-foreground lg:text-lg">
              Everything you need to know<br className="sm:hidden" /> about working with Mind Media.
            </p>
          </div>
        </div>

        <div className="mx-auto mt-12 grid max-w-6xl gap-x-12 gap-y-2 md:grid-cols-2">
          <FaqColumn items={col1} />
          <FaqColumn items={col2} />
        </div>

        {leftItems.length > initialVisible && (
          <div className="mt-8 flex justify-center">
            <Button variant="outline" onClick={() => setShowAll((v) => !v)}>
              {showAll ? "Show less" : `Load more (${leftItems.length - initialVisible})`}
            </Button>
          </div>
        )}
      </div>
      </div>
    </section>
  );
}

function FaqColumn({ items }: { items: FaqItem[] }) {
  return (
    <Accordion
      type="single"
      collapsible
      className="w-full"
      onValueChange={(value) => {
        if (value) event("faq_open", { question_id: value });
      }}
    >
      {items.map((item) => (
        <AccordionItem key={item.id} value={item.id} className="border-b">
          <AccordionTrigger className="text-left text-base font-medium text-gray-950 md:text-lg">
            {item.question}
          </AccordionTrigger>
          <AccordionContent>
            <p className="text-muted-foreground text-base">{item.answer}</p>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
